import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getFirestore, doc, setDoc, onSnapshot, serverTimestamp, collection, addDoc, query, orderBy, deleteDoc, limit } from 'firebase/firestore';
import { Plus, XCircle, Zap, Edit, Trash2, Video, Mic, MicOff, ArrowRight, Lock, Star, LayoutGrid, Map, TrendingUp, Check, CheckCircle, Briefcase, ChevronRight, Lightbulb, Target, BookOpen } from 'lucide-react';
import { apiCall, getErrorMessage } from './utils/api';
import { db } from './firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- SHARED COMPONENTS (palette-only: #3C0F6C, #fafafa, #d5e7f3) ---
const Input = ({ label, ...props }) => (
  <div className="space-y-2 w-full">
    {label && <label className="text-sm font-medium text-[#3C0F6C]/80">{label}</label>}
    <input
      {...props}
      className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
    />
  </div>
);

const TextArea = ({ label, ...props }) => (
  <div className="space-y-2">
    {label && <label className="text-sm font-medium text-[#3C0F6C]/80">{label}</label>}
    <textarea
      {...props}
      rows="4"
      className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
    ></textarea>
  </div>
);

const formatResumeDataAsText = (data,user) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;
  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
  });
  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
  });
  text += "\n## Projects\n";
  (data.projects || []).forEach(proj => {
    text += `- ${proj.title} (${proj.period})\n  ${proj.description}\n`;
  });
  text += "\n## Skills\n";
  {
    const skills = Array.isArray(data.skills)
      ? data.skills.filter(Boolean).map(String)
      : (typeof data.skills === 'string'
        ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
        : []);
    text += skills.join(', ');
  }
  text += "\n\n## Achievements\n";
  (data.achievements || []).forEach(ach => {
    text += `- ${ach.description}\n`;
  });
  text += "\n## Awards\n";
  (data.awards || []).forEach(award => {
    text += `- ${award.description}\n`;
  });
  text += "\n## Certificates\n";
  (data.certificates || []).forEach(cert => {
    text += `- ${cert.description}\n`;
  });
  text += "\n## Hobbies\n";
  (data.hobbies || []).forEach(hobby => {
    text += `- ${hobby.description}\n`;
  });
  return text;
};

const CareerPath = ({ user, userData, resumeData, appId, externalActiveTab, onTabChange, showOnlyBasic = false, showOnlyAdvanced = false }) => {
  // All hooks must be declared first (Rules of Hooks)

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [internalActiveTab, setInternalActiveTab] = useState('basic');
  const [userId] = useState(user ? user.uid : 'anonymous');
  // Check if user is premium, if not show upgrade modal and block access the limit
  // if (!userData || userData.role !== 'premium') {
  //   const headerTitle = showOnlyBasic ? 'Map Analysis' : (showOnlyAdvanced ? 'Advanced AI Counselor' : 'Career Path');
  //   return (
  //     <div>
  //       <header className="mb-8">
  //         <h2 className="text-3xl font-bold text-[#3C0F6C] dark:text-[#bca1da]">{headerTitle}</h2>
  //         <p className="mt-1 text-[#3C0F6C]/70 dark:text-[#bca1da]/80">Discover personalized career paths and get expert guidance.</p>
  //       </header>
  //       <div className="flex flex-col items-center justify-center min-h-[400px] bg-[#fafafa] rounded-xl border border-[#d5e7f3] p-8">
  //         <Lock size={64} className="text-[#3C0F6C] mb-4" />
  //         <h3 className="text-2xl font-bold text-[#3C0F6C] mb-2">Premium Feature</h3>
  //         <p className="text-[#3C0F6C]/80 mb-6 text-center max-w-md">
  //           This feature is available for premium users only. Upgrade your account to access Career Path features.
  //         </p>
  //         <button
  //           onClick={() => setIsUpgradeModalOpen(true)}
  //           className="bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 px-6 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors"
  //         >
  //           Upgrade to Premium
  //         </button>
  //       </div>
  //       <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
  //     </div>
  //   );
  // }

  // Use external tab if provided, otherwise use internal state
  const activeTab = externalActiveTab || internalActiveTab;

  const handleTabChange = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  // Choose header text depending on requested mode
  const headerTitle = showOnlyBasic ? 'Map Analysis' : (showOnlyAdvanced ? 'Advanced AI Counselor' : (activeTab === 'basic' ? 'Basic Guidance' : 'Advanced AI Counselor'));
  const headerDesc = showOnlyBasic
    ? 'Visualize career paths and explore opportunities based on your profile.'
    : 'Discover personalized career paths and get expert guidance.';

  return (
    <div>
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-[#3C0F6C] dark:text-[#bca1da]">{headerTitle}</h2>
        <p className="mt-1 text-[#3C0F6C]/70 dark:text-[#bca1da]/80">{headerDesc}</p>
      </header>

      {/* If a parent requested only one mode, hide the tab nav and render the matching content only. */}
      {!showOnlyBasic && !showOnlyAdvanced && (
        <div className="mb-4 border-b border-[#d5e7f3]">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => handleTabChange('basic')}
              className={`py-3 px-1 border-b-2 font-semibold text-sm ${activeTab === 'basic'
                ? 'border-[#3C0F6C] text-[#3C0F6C]'
                : 'border-transparent text-[#3C0F6C]/70 hover:text-[#3C0F6C] hover:border-[#d5e7f3]'
                }`}
            >
              Basic Guidance
            </button>
            <button
              onClick={() => handleTabChange('advanced')}
              className={`relative py-3 px-1 border-b-2 font-semibold text-sm ${activeTab === 'advanced'
                ? 'border-[#3C0F6C] text-[#3C0F6C]'
                : 'border-transparent text-[#3C0F6C]/70 hover:text-[#3C0F6C] hover:border-[#d5e7f3]'
                }`}
            >
              Advanced AI Counselor
              {userData?.role !== 'premium' && <Lock size={12} className="absolute top-1 right-[-1rem] text-[#3C0F6C]" />}
            </button>
          </nav>
        </div>
      )}

      {showOnlyBasic ? (
        <BasicCareerGuidance resumeData={resumeData} user={user}/>
      ) : showOnlyAdvanced ? (
        <CareerChatbot user={user} resumeData={resumeData} db={db} appId={appId} />
      ) : (
        activeTab === 'basic' ? (
          <BasicCareerGuidance resumeData={resumeData} user={user}/>
        ) : (
          <CareerChatbot user={user} resumeData={resumeData} db={db} appId={appId} />
        )
      )}

      <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
    </div>
  );
};

const BasicCareerGuidance = ({ resumeData,user }) => {

  const [interests, setInterests] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showIntro, setShowIntro] = useState(() => {
    try { return localStorage.getItem('careerBasicIntroDismissed') !== '1'; } catch (err) {
      console.error('Failed to read localStorage', err);
      return true; }
  });

  const handleShowBasicGuide = () => {
    setShowIntro(true);
    try { localStorage.removeItem('careerBasicIntroDismissed'); } catch(err) {
      console.error('Failed to update localStorage', err);
    }
  };

  const handleGetAnalysis = async (userId) => {

    setIsLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append('resume_text', formatResumeDataAsText(resumeData));
    formData.append('user_interests', interests);

    try {
      const { data } = await apiCall('/api/career/full-analysis', {
        method: 'POST',
        body: formData,
      });
      console.log('full analyss:', data);
      setAnalysisResult(data.analysis);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-end -mt-20">
        <button onClick={handleShowBasicGuide} title="Show page instructions" className="mb-2 border border-[#d5e7f3] text-[#3C0F6C] bg-white font-semibold py-2 px-4 rounded-xl hover:bg-[#f3f0fa] transition-colors">
          Help?
        </button>
      </div>
      {showIntro && (
        <div className="bg-[#ede7f6] p-4 rounded-xl border border-[#d5e7f3] text-[#3C0F6C]">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="font-semibold">What this page does</p>
              <p className="text-sm">Map your potential career paths based on your resume and interests. Get a visual roadmap, role progression, and skills to develop.</p>
              <p className="font-semibold mt-2">How to use it</p>
              <ul className="list-decimal pl-5 text-sm space-y-1">
                <li>Describe your interests and aspirations below.</li>
                <li>Click <strong>Get Full Analysis</strong> to generate your roadmap.</li>
                <li>Explore the graph and review recommended skills and next steps.</li>
              </ul>
            </div>
            <button
              onClick={() => { setShowIntro(false); try { localStorage.setItem('careerBasicIntroDismissed','1'); } catch(err) {
                console.error('Failed to update localStorage', err);
              } }}
              className="shrink-0 text-sm px-3 py-1 rounded-lg border bg-[#FFD600] border-[#FFD600] text-[#0E0E0E] hover:bg-[#e6c900]"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      <div className="bg-[#fafafa] p-6 rounded-xl border border-[#d5e7f3] shadow-sm">
        <h3 className="text-xl font-bold mb-4 text-[#3C0F6C]">Find Your Path</h3>
        <TextArea
          label="Your Interests & Aspirations"
          placeholder="e.g., I enjoy building user interfaces, working with data, and solving complex problems..."
          value={interests}
          onChange={(e) => setInterests(e.target.value)}
        />
        <button
          onClick={()=>handleGetAnalysis(user?.uid)}
          disabled={isLoading || !interests}
          className="mt-4 bg-[#3C0F6C] text-[#fafafa] font-semibold py-2 px-6 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Analyzing...' : 'Get Full Analysis'}
        </button>
      </div>

      {error && <p className="text-sm text-[#3C0F6C] bg-[#d5e7f3] p-3 rounded-xl">{error}</p>}

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <svg className="animate-spin h-8 w-8 text-[#3C0F6C]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          <p className="ml-4 text-[#3C0F6C]/80">Generating your personalized career roadmap...</p>
        </div>
      )}

      {analysisResult && (
        <div>
          <h3 className="text-2xl font-bold text-[#3C0F6C] mb-4">Your Career Roadmap</h3>
          <CareerGraph analysis={analysisResult} userName={resumeData.personalInfo.name} />
          <div className="mt-8 space-y-6">
            {analysisResult.map((rec, index) => (
              <CareerPathCard key={index} pathData={rec} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CareerGraph = ({ analysis, userName }) => {
  const scrollContainerRef = useRef(null);
  const [graph, setGraph] = useState({ nodes: [], links: [] });
  const [graphSize, setGraphSize] = useState({ width: 1200, height: 800 });

  const calculateLayout = useCallback(() => {
    if (!analysis || analysis.length === 0) return;

    const progressionOrder = {
      "entry-level": 1, "entry": 1, "junior": 2, "mid-level": 3, "mid": 3,
      "intermediate": 3, "senior": 4, "lead": 5, "staff": 6, "principal": 7,
      "manager": 8, "director": 9,
    };

    let maxDepth = 0;
    analysis.forEach(path => {
      if (path.progression) {
        path.progression.sort((a, b) => {
          const levelA = a.level.toLowerCase().replace(' ', '-');
          const levelB = b.level.toLowerCase().replace(' ', '-');
          return (progressionOrder[levelA] || 99) - (progressionOrder[levelB] || 99);
        });
      }
      if (path.progression && path.progression.length > maxDepth) {
        maxDepth = path.progression.length;
      }
    });

    const requiredWidth = Math.max(1200, analysis.length * 350);
    const requiredHeight = 100 + 100 + ((maxDepth + 1) * 120) + 50;
    setGraphSize({ width: requiredWidth, height: requiredHeight });

    const nodes = [];
    const links = [];

    const userNode = { id: 'user', name: userName, x: requiredWidth / 2, y: 50, isUser: true };
    nodes.push(userNode);

    const numPaths = analysis.length;
    const horizontalSpacing = requiredWidth / (numPaths + 1);

    analysis.forEach((path, pathIndex) => {
      const pathX = horizontalSpacing * (pathIndex + 1);

      const pathNode = {
        id: path.career_path,
        name: path.career_path,
        x: pathX,
        y: 170,
      };
      nodes.push(pathNode);
      links.push({ source: userNode, target: pathNode });

      (path.progression || []).forEach((level, levelIndex) => {
        const levelNode = {
          id: `${path.career_path}-${level.level}`,
          name: level.roles.join(' / '),
          x: pathX,
          y: 170 + (levelIndex + 1) * 120,
        };
        nodes.push(levelNode);
        const sourceNode = levelIndex === 0 ? pathNode : nodes.find(n => n.id === `${path.career_path}-${path.progression[levelIndex - 1].level}`);
        if (sourceNode) {
          links.push({ source: sourceNode, target: levelNode });
        }
      });
    });

    setGraph({ nodes, links });

  }, [analysis, userName]);

  useEffect(() => {
    calculateLayout();
    window.addEventListener('resize', calculateLayout);
    return () => window.removeEventListener('resize', calculateLayout);
  }, [calculateLayout]);

  if (!analysis || analysis.length === 0) return null;

  return (
    <div
      ref={scrollContainerRef}
      className="overflow-auto h-[600px] bg-[#fafafa] border border-[#d5e7f3] rounded-xl p-4"
    >
      <div
        className="relative"
        style={{ width: graphSize.width, height: graphSize.height }}
      >
        <svg className="absolute top-0 left-0 w-full h-full" style={{ zIndex: 0 }}>
          {graph.links.map((link, i) => (
            <line
              key={i}
              x1={link.source.x}
              y1={link.source.y}
              x2={link.target.x}
              y2={link.target.y}
              className="stroke-[#d5e7f3]"
              strokeWidth="2"
              strokeDasharray="6,6"
            />
          ))}
        </svg>
        {graph.nodes.map((node) => (
          <div
            key={node.id}
            className={`absolute p-4 text-center rounded-xl shadow flex items-center justify-center transition-all duration-500 border ${node.isUser
              ? 'bg-[#d5e7f3] text-[#3C0F6C] border-[#d5e7f3] font-bold'
              : 'bg-[#fafafa] border-[#d5e7f3] text-[#3C0F6C]'
              }`}
            style={{
              transform: 'translate(-50%, -50%)',
              left: `${node.x}px`,
              top: `${node.y}px`,
              zIndex: 1,
              width: 220,
              minHeight: 70,
            }}
          >
            <p className="text-sm font-semibold break-words">{node.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const CircularProgress = ({ score }) => {
  const size = 60; // unchanged logic
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="stroke-current"
          stroke="#d5e7f3"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="transition-all duration-500"
          stroke="#3C0F6C"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-[#3C0F6C]">
        {score}
      </span>
    </div>
  );
};

const ProgressionStep = ({ level, roles, isLast }) => (
  <div className="flex">
    <div className="flex flex-col items-center mr-4">
      <div>
        <div className="flex items-center justify-center w-8 h-8 bg-[#3C0F6C] rounded-full">
          <TrendingUp size={16} className="text-[#fafafa]" />
        </div>
      </div>
      {!isLast && <div className="w-px h-full bg-[#d5e7f3]"></div>}
    </div>
    <div className="pb-8">
      <p className="font-semibold text-[#3C0F6C]">{level}</p>
      <p className="text-sm text-[#3C0F6C]/80">{roles.join(' / ')}</p>
    </div>
  </div>
);

const CareerPathCard = ({ pathData }) => {
  return (
    <div className="bg-[#fafafa] p-6 rounded-xl border border-[#d5e7f3] shadow-sm">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h3 className="text-2xl font-bold text-[#3C0F6C]">{pathData.career_path}</h3>
          <p className="text-sm text-[#3C0F6C]/80 mt-2">{pathData.reasoning}</p>
        </div>
        <div className="flex flex-col items-center flex-shrink-0">
          <CircularProgress score={pathData.compatibility_score} />
          <p className="text-xs font-semibold text-[#3C0F6C]/70 mt-1">Compatibility</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-[#3C0F6C] mb-3">Progression Path</h4>
          <div>
            {(pathData.progression || []).map((step, index) => (
              <ProgressionStep
                key={index}
                level={step.level}
                roles={step.roles}
                isLast={index === pathData.progression.length - 1}
              />
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-[#3C0F6C] mb-3">Skill Analysis</h4>
          <div className="space-y-4">
            <div>
              <h5 className="text-sm font-semibold text-[#3C0F6C]/80 mb-2">Your Current Skills</h5>
              <div className="flex flex-wrap gap-2">
                {(pathData.skill_gap?.current_skills || []).map((skill, i) => (
                  <span key={i} className="flex items-center gap-1.5 bg-[#d5e7f3] text-[#3C0F6C] text-xs font-medium px-2 py-1 rounded-full">
                    <Check size={12} /> {skill}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-sm font-semibold text-[#3C0F6C]/80 mb-2">Skills to Develop</h5>
              <div className="flex flex-wrap gap-2">
                {(pathData.skill_gap?.skills_to_develop || []).map((item, i) => (
                  <span key={i} title={item.reasoning} className="flex items-center gap-1.5 bg-[#d5e7f3]/50 text-[#3C0F6C] text-xs font-medium px-2 py-1 rounded-full cursor-help">
                    <Plus size={12} /> {item.skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobListingCard = ({ job }) => {
  const { title, company, location, redirect_url, match_details } = job;
  const { match_score, reasoning, suggestions } = match_details || {};

  return (
    <div className="bg-[#fafafa] p-4 rounded-xl border border-[#d5e7f3] flex flex-col justify-between hover:shadow transition-shadow">
      <div>
        <div className="flex justify-between items-start gap-4">
          <div>
            <h4 className="font-bold text-[#3C0F6C]">{title}</h4>
            <p className="text-sm text-[#3C0F6C]/80">{company}</p>
            <p className="text-xs text-[#3C0F6C]/70 mt-1">{location}</p>
          </div>
          <div className="flex-shrink-0 text-center">
            <CircularProgress score={match_score || 0} />
            <p className="text-xs font-semibold text-[#3C0F6C]/70 mt-1">Match</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#d5e7f3]">
          <h5 className="text-xs font-semibold text-[#3C0F6C] mb-2 flex items-center gap-1.5"><Lightbulb size={14} className="text-[#3C0F6C]" /> AI Insight</h5>
          <p className="text-xs text-[#3C0F6C]/80 italic">"{reasoning}"</p>
        </div>
        <div className="mt-3">
          <h5 className="text-xs font-semibold text-[#3C0F6C] mb-2 flex items-center gap-1.5"><Target size={14} className="text-[#3C0F6C]" /> How to Stand Out</h5>
          <ul className="space-y-1">
            {(suggestions || []).slice(0, 2).map((suggestion, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-[#3C0F6C]/80">
                <CheckCircle size={12} className="text-[#3C0F6C] mt-0.5 flex-shrink-0" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <a
        href={redirect_url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 block w-full text-center bg-[#3C0F6C] text-[#fafafa] font-semibold py-2 rounded-xl hover:bg-[#3C0F6C]/90 text-sm transition-colors"
      >
        View & Apply
      </a>
    </div>
  );
};

const SkillCard = ({ skill }) => {
  // Handle both formats: existing format (text/children) and backend format (skill/description)
  const skillName = skill.text || skill.skill || skill.name || 'Unknown Skill';
  const description = skill.children?.[0]?.text || skill.description || '';
  
  return (
    <div className="bg-[#fafafa] p-4 rounded-xl border border-[#d5e7f3] flex flex-col justify-between hover:shadow transition-shadow">
      <div>
        <div className="flex items-center gap-3">
          <div className="bg-[#d5e7f3] p-2 rounded-full">
            <Star size={16} className="text-[#3C0F6C]" />
          </div>
          <h4 className="font-bold text-[#3C0F6C]">{skillName}</h4>
        </div>
        {description && (
          <p className="text-sm text-[#3C0F6C]/80 mt-2 pl-1">{description}</p>
        )}
      </div>
      <a
        href={`https://www.google.com/search?q=learn+${encodeURIComponent(skillName)}+courses`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 w-full text-center bg-[#d5e7f3] text-[#3C0F6C] font-semibold py-2 rounded-xl hover:bg-[#fafafa] border border-[#d5e7f3] text-sm transition-colors flex items-center justify-center gap-2"
      >
        <BookOpen size={14} />
        Find Courses
      </a>
    </div>
  );
};

const SkillsCardDisplay = ({ skills }) => {
  if (!skills || !Array.isArray(skills) || skills.length === 0) {
    return null;
  }

  return (
    <div className="my-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {skills.map((skillItem, index) => (
          <SkillCard key={index} skill={skillItem} />
        ))}
      </div>
    </div>
  );
};

const VisualResponse = ({ data }) => {
  if (!data || !data.title) return null;

  const isSkillList = data.items.every(item => item.children && item.children.length > 0);

  if (isSkillList) {
    return (
      <div className="my-4 p-4 bg-[#d5e7f3]/40 border border-[#d5e7f3] rounded-xl">
        <h4 className="text-md font-bold text-[#3C0F6C] mb-4">{data.title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.items.map((item, index) => (
            <SkillCard key={index} skill={item} />
          ))}
        </div>
      </div>
    );
  }

  const renderItems = (items, level = 0) => (
    <ul className={level > 0 ? 'pl-4' : ''}>
      {items.map((item, index) => (
        <li key={index} className="relative pl-6">
          <div className="absolute left-0 top-0 flex items-center justify-center w-5 h-5 bg-[#d5e7f3] rounded-full">
            <div className="w-2 h-2 bg-[#3C0F6C] rounded-full"></div>
          </div>
          {index < items.length - 1 && <div className="absolute left-[9px] top-5 w-px h-[calc(100%-1rem)] bg-[#d5e7f3]"></div>}
          <div className="ml-4 py-2">
            <p className="text-sm font-semibold text-[#3C0F6C]">{item.text}</p>
            {item.children && item.children.length > 0 && (
              <div className="mt-2">{renderItems(item.children, level + 1)}</div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="my-4 p-4 bg-[#d5e7f3]/40 border border-[#d5e7f3] rounded-xl">
      <h4 className="text-md font-bold text-[#3C0F6C] mb-4">{data.title}</h4>
      {renderItems(data.items)}
    </div>
  );
};

const CareerChatbot = ({ user, resumeData, db, appId }) => {

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [careerData, setCareerData] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const chatEndRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showIntro, setShowIntro] = useState(() => {
    try { return localStorage.getItem('careerAdvancedIntroDismissed') !== '1'; } catch(err) {
      console.error('Failed to read localStorage', err);
      return true; }
  });
  const handleShowGuide = () => {
    setShowIntro(true);
    try { localStorage.removeItem('careerAdvancedIntroDismissed'); } catch(err) {
      console.error('Failed to update localStorage', err);
    }
  };

  useEffect(() => {
    if (user) {
      const q = query(collection(db, `artifacts/${appId}/users/${user.uid}/career_chats`), orderBy('createdAt', 'desc'), limit(15));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const history = [];
        querySnapshot.forEach((doc) => {
          history.push({ id: doc.id, ...doc.data() });
        });
        setChatHistory(history);
        if (isInitialLoad) {
          if (history.length > 0) {
            loadChat(history[0]);
          } else {
            startNewChat();
          }
          setIsInitialLoad(false);
        }
      });
      return () => unsubscribe();
    }
  }, [user, db, appId, isInitialLoad]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, careerData]);

  const startNewChat = () => {
    setActiveChatId(null);
    setMessages([{ role: 'assistant', content: "Hello! I'm Kris, your personal career counselor. Ask me to suggest some career paths, or find job openings for a specific role." }]);
    setCareerData(null);
  };

  const loadChat = (chat) => {
    setActiveChatId(chat.id);
    setMessages(chat.messages);
    const lastMessage = chat.messages[chat.messages.length - 1];
    if (lastMessage && lastMessage.careerData) {
      setCareerData(lastMessage.careerData);
    } else {
      setCareerData(null);
    }
  };

  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, `artifacts/${appId}/users/${user.uid}/career_chats`, chatId));
        if (activeChatId === chatId) {
          const remainingChats = chatHistory.filter(c => c.id !== chatId);
          if (remainingChats.length > 0) {
            loadChat(remainingChats[0]);
          } else {
            startNewChat();
          }
        }
      } catch (error) {
        console.error('Career chat: Error deleting chat', error);
      }
    }
  };

  const parseMarkdownList = (text) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2 || !lines.some(line => line.trim().startsWith('*') || line.trim().startsWith('-'))) {
      return null;
    }

    const title = lines[0].replace(/#+\s*|\*+/g, '').trim();
    const items = [];
    const stack = [{ level: -1, children: items }];

    lines.slice(1).forEach(line => {
      const indent = line.search(/\S|$/);
      const text = line.trim().substring(line.trim().indexOf(' ') + 1);
      const node = { text, children: [] };

      while (indent <= stack[stack.length - 1].level) {
        stack.pop();
      }

      stack[stack.length - 1].children.push(node);
      stack.push({ level: indent, children: node.children });
    });

    return { title, items };
  };

  const handleSendMessage = async (e) => {

    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);
    setCareerData(null);

    const formData = new FormData();
    formData.append('resume_text', formatResumeDataAsText(resumeData));

    const historyForApi = updatedMessages.slice(1, -1).map(msg => {
      let content = '';
      if (msg.content) {
        content = msg.content;
      } else if (msg.careerData) {
        content = '[AI presented career path visualization]';
      } else if (msg.jobListings) {
        content = '[AI presented job listings]';
      } else if (msg.visualData) {
        content = `[AI presented visual list: ${msg.visualData.title}]`;
      }
      return { role: msg.role, content: content };
    });

    formData.append('history', JSON.stringify(historyForApi));
    formData.append('user_message', input);

    try {
      const { data } = await apiCall('/api/career/chat', {
        method: 'POST',
        body: formData,
      });

      console.log('API response data:', data);
      let aiMessage;

      const tryExtractJson = (text) => {
        if (typeof text !== 'string') return null;
        let candidate = text.trim();
        // Remove markdown code fences if present
        const fenceMatch = candidate.match(/```(?:json|js|javascript)?[\r\n]+([\s\S]*?)```/i);
        if (fenceMatch) {
          candidate = fenceMatch[1].trim();
        }
        // If still has leading prose, try to slice first JSON-looking block
        const firstBrace = candidate.indexOf('{');
        const firstBracket = candidate.indexOf('[');
        let start = -1;
        if (firstBrace !== -1 && firstBracket !== -1) start = Math.min(firstBrace, firstBracket);
        else start = Math.max(firstBrace, firstBracket);
        if (start > 0) candidate = candidate.slice(start);
        // Try parse full string
        try {
          return JSON.parse(candidate);
        } catch (err) {

             console.warn('Full JSON parse failed, trying substring extraction:', err);  
         }
        // Try to find last closing brace/bracket and parse substring
        const lastBrace = candidate.lastIndexOf('}');
        const lastBracket = candidate.lastIndexOf(']');
        let end = Math.max(lastBrace, lastBracket);
        if (end > 0) {
          try {
            return JSON.parse(candidate.slice(0, end + 1));
          } catch (err) {

              console.error('Substring JSON parse failed:', err);
           }
        }
        return null;
      };

      const tryExtractRecommendationsArray = (text) => {
        if (typeof text !== 'string') return null;
        const lower = text.toLowerCase();
        const keyIndex = lower.indexOf('"recommendations"');
        if (keyIndex === -1) return null;
        const afterKey = text.indexOf('[', keyIndex);
        if (afterKey === -1) return null;
        // Bracket counting to find the end of the array
        let depth = 0;
        let endIndex = -1;
        for (let i = afterKey; i < text.length; i++) {
          const ch = text[i];
          if (ch === '[') depth++;
          else if (ch === ']') {
            depth--;
            if (depth === 0) { endIndex = i; break; }
          }
        }
        if (endIndex === -1) return null;
        const arraySlice = text.slice(afterKey, endIndex + 1);
        try {
          const arr = JSON.parse(arraySlice);
          return Array.isArray(arr) ? arr : null;
        } catch (err) {


          console.error('Error parsing recommendations array slice:', err);
          return null;
        }
      };

      const tryExtractRecommendationObjects = (text) => {
        if (typeof text !== 'string') return null;
        const lower = text.toLowerCase();
        const keyIndex = lower.indexOf('"recommendations"');
        if (keyIndex === -1) return null;
        const arrayStart = text.indexOf('[', keyIndex);
        if (arrayStart === -1) return null;
        const result = [];
        let i = arrayStart + 1;
        let inString = false;
        let escape = false;
        let braceDepth = 0;
        let current = '';
        for (; i < text.length; i++) {
          const ch = text[i];
          if (inString) {
            current += ch;
            if (escape) {
              escape = false;
            } else if (ch === '\\') {
              escape = true;
            } else if (ch === '"') {
              inString = false;
            }
            continue;
          }
          if (ch === '"') {
            inString = true;
            current += ch;
            continue;
          }
          if (ch === '{') {
            braceDepth++;
            current += ch;
            continue;
          }
          if (ch === '}') {
            braceDepth--;
            current += ch;
            if (braceDepth === 0) {
              try {
                const obj = JSON.parse(current);
                result.push(obj);
              } catch (err) {
                console.error('Error parsing recommendation object:', err);
               }
              current = '';
            }
            continue;
          }
          if (braceDepth > 0) {
            current += ch;
          } else if (braceDepth === 0 && ch === ']') {
            break;
          }
        }
        return result.length > 0 ? result : null;
      };

      if (data.type === 'job_listings') {
        aiMessage = {
          role: 'assistant',
          content: "I found some job openings that might interest you. Here's a detailed analysis:",
          jobListings: data.jobs
        };
        setCareerData(null);
      } else if (data.type === 'career_recommendations') {
        setCareerData(data.data);
        aiMessage = {
          role: 'assistant',
          content: 'Ive generated a few potential career paths for you. Here is a simplified breakdown. You can also switch to a map view for a broader perspective.',
          careerData: data.data
        };
      } else if (data.type === 'skills_card') {
        // Handle skills_card response type
        const skillsData = Array.isArray(data.data) ? data.data : [];
        aiMessage = {
          role: 'assistant',
          content: skillsData.length > 0 
            ? "Here are some skill recommendations to help you advance in your career:" 
            : "I have prepared some skill recommendations for you.",
          skillsCard: skillsData
        };
        setCareerData(null);
      } else if (data.response) {
        // Try to detect JSON payloads in string responses (e.g., career recommendations)
        let parsed = tryExtractJson(data.response);

        if (parsed && (Array.isArray(parsed) || Array.isArray(parsed.recommendations))) {
          const recs = Array.isArray(parsed) ? parsed : parsed.recommendations;
          setCareerData(recs);
          aiMessage = {
            role: 'assistant',
            content: 'Here are tailored career path recommendations based on your profile.',
            careerData: recs
          };
        } else {
          // Fallback: extract just the recommendations array from mixed text or incomplete JSON
          const recsOnly = tryExtractRecommendationsArray(data.response) || tryExtractRecommendationObjects(data.response);
          if (recsOnly && recsOnly.length > 0) {
            setCareerData(recsOnly);
            aiMessage = {
              role: 'assistant',
              content: 'Here are tailored career path recommendations based on your profile.',
              careerData: recsOnly
            };
          } else {
            const visualData = parseMarkdownList(data.response);
            if (visualData && visualData.items.length > 0) {
              aiMessage = { role: 'assistant', content: null, visualData: visualData };
            } else {
              // Ensure we have a non-empty response
              const responseText = data.response?.trim() || '';
              if (responseText) {
                aiMessage = { role: 'assistant', content: responseText };
              } else {
                // Fallback for empty response - try to extract from other fields
                console.warn('Career chat: Received empty response field', data);
                
                // Try to extract content from other possible fields
                let fallbackText = '';
                if (data?.text && typeof data.text === 'string' && data.text.trim()) {
                  fallbackText = data.text.trim();
                } else if (data?.message && typeof data.message === 'string' && data.message.trim()) {
                  fallbackText = data.message.trim();
                } else if (data && typeof data === 'object') {
                  const textFields = Object.values(data).filter(v => typeof v === 'string' && v.trim().length > 0);
                  if (textFields.length > 0) {
                    fallbackText = textFields[0].trim();
                  }
                }
                
                aiMessage = { 
                  role: 'assistant', 
                  content: fallbackText || "I'm here to help! Could you please rephrase your question?" 
                };
              }
            }
          }
        }
      } else {
        // Log unexpected format for debugging, but always try to show model's response
        console.error('Career chat: Unhandled response format', {
          timestamp: new Date().toISOString(),
          responseKeys: data ? Object.keys(data) : [],
          hasType: data?.type !== undefined,
          hasResponse: data?.response !== undefined
        });
        
        // Always try to extract and display the model's response
        // Backend should always return a response field, but handle edge cases gracefully
        let fallbackContent = '';
        
        // Priority 1: Check for response field (backend always includes this)
        if (data?.response && typeof data.response === 'string' && data.response.trim()) {
          fallbackContent = data.response.trim();
        }
        // Priority 2: Check for other common text fields
        else if (data?.text && typeof data.text === 'string' && data.text.trim()) {
          fallbackContent = data.text.trim();
        }
        else if (data?.message && typeof data.message === 'string' && data.message.trim()) {
          fallbackContent = data.message.trim();
        }
        // Priority 3: Extract any string value from the response object
        else if (data && typeof data === 'object') {
          const textFields = Object.values(data).filter(v => typeof v === 'string' && v.trim().length > 0);
          if (textFields.length > 0) {
            fallbackContent = textFields[0].trim();
          }
        }
        // Priority 4: If data itself is a string
        else if (typeof data === 'string' && data.trim()) {
          fallbackContent = data.trim();
        }
        
        // Final fallback: Only show generic message if we truly have nothing
        if (!fallbackContent) {
          console.warn('Career chat: No extractable content from response', data);
          fallbackContent = "I'm here to help! Could you please rephrase your question?";
        }
        
        aiMessage = {
          role: 'assistant',
          content: fallbackContent
        };
      }

      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);

      const chatToSave = {
        messages: finalMessages,
        createdAt: serverTimestamp(),
        title: finalMessages.find(m => m.role === 'user')?.content.substring(0, 30) || 'New Chat'
      };

      if (activeChatId) {
        await setDoc(doc(db, `artifacts/${appId}/users/${user.uid}/career_chats`, activeChatId), chatToSave, { merge: true });
      } else {
        const newDocRef = await addDoc(collection(db, `artifacts/${appId}/users/${user.uid}/career_chats`), chatToSave);
        setActiveChatId(newDocRef.id);
      }

    } catch (error) {
      const errorMessage = { role: 'assistant', content: `Sorry, I encountered an error: ${error.message}` };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      <div className="lg:col-span-1 bg-[#fafafa] p-4 rounded-xl border border-[#d5e7f3] shadow-sm">
        <button onClick={startNewChat} className="w-full mb-2 bg-[#3C0F6C] text-[#fafafa] font-semibold py-2 px-4 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors flex items-center justify-center gap-2">
          <Plus size={18} />
          <span>New Chat</span>
        </button>
        <button onClick={handleShowGuide} title="Show page instructions" className="w-full mb-4 border border-[#d5e7f3] text-[#3C0F6C] bg-white font-semibold py-2 px-4 rounded-xl hover:bg-[#f3f0fa] transition-colors">
          Help?
        </button>
        <div className="space-y-2">
          {chatHistory.map(chat => (
            <div key={chat.id} className="group relative">
              <button onClick={() => loadChat(chat)} className={`w-full text-left p-2 rounded-xl text-sm truncate pr-8 border ${activeChatId === chat.id ? 'bg-[#d5e7f3] text-[#3C0F6C] border-[#d5e7f3]' : 'text-[#3C0F6C]/80 hover:bg-[#d5e7f3]/40 border-transparent'}`}>
                {chat.title}
              </button>
              <button
                onClick={(e) => handleDeleteChat(e, chat.id)}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#3C0F6C]/70 hover:bg-[#d5e7f3] hover:text-[#3C0F6C] opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3 bg-[#fafafa] rounded-xl border border-[#d5e7f3] shadow-sm flex flex-col h-[70vh]">
        {showIntro && (
          <div className="m-4 bg-[#ede7f6] p-4 rounded-xl border border-[#d5e7f3] text-[#3C0F6C]">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="font-semibold">What this page does</p>
                <p className="text-sm">Discover personalized career paths, view skill gaps, and get job suggestions using your resume context.</p>
                <p className="font-semibold mt-2">How to use it</p>
                <ul className="list-decimal pl-5 text-sm space-y-1">
                  <li>Click <strong>New Chat</strong> to start.</li>
                  <li>Ask for career paths or job openings. Include preferences (location, domain, seniority).</li>
                  <li>Switch between <strong>Card View</strong> and <strong>Map View</strong> for visual insights.</li>
                  <li>Open job cards and <strong>View & Apply</strong> to navigate to listings.</li>
                </ul>
              </div>
              <button
                onClick={() => { setShowIntro(false); try { localStorage.setItem('careerAdvancedIntroDismissed','1'); } catch(err) {
                  console.error('Failed to update localStorage', err);
                } }}
                className="shrink-0 text-sm px-3 py-1 rounded-lg border bg-[#FFD600] border-[#FFD600] text-[#0E0E0E] hover:bg-[#e6c900]"
              >
                Got it
              </button>
            </div>
          </div>
        )}
        <div className="flex-grow overflow-y-auto p-4 space-y-4">
          {messages.map((msg, index) => (
            <div key={index}>
              {msg.role === 'user' ? (
                <div className="flex justify-end">
                  <div className="max-w-lg p-3 rounded-2xl bg-[#3C0F6C] text-[#fafafa]">
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ) : (
                <>
                  {msg.content && (
                    <div className="flex justify-start">
                      <div className="max-w-lg p-3 rounded-2xl bg-[#d5e7f3]/40 text-[#3C0F6C] border border-[#d5e7f3]">
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  )}
                  {msg.visualData && <VisualResponse data={msg.visualData} />}
                  {msg.careerData && (
                    <div className="my-4">
                      <div className="flex justify-center mb-4 p-1 bg-[#d5e7f3]/40 rounded-lg w-min mx-auto border border-[#d5e7f3]">
                        <button onClick={() => setViewMode('cards')} className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors flex items-center gap-2 ${viewMode === 'cards' ? 'bg-[#d5e7f3] text-[#3C0F6C]' : 'text-[#3C0F6C]/80'}`}>
                          <LayoutGrid size={16} /> Card View
                        </button>
                        <button onClick={() => setViewMode('map')} className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors flex items-center gap-2 ${viewMode === 'map' ? 'bg-[#d5e7f3] text-[#3C0F6C]' : 'text-[#3C0F6C]/80'}`}>
                          <Map size={16} /> Map View
                        </button>
                      </div>
                      {viewMode === 'cards' ? (
                        <div className="space-y-6">
                          {msg.careerData.map((path, i) => <CareerPathCard key={i} pathData={path} />)}
                        </div>
                      ) : (
                        <CareerGraph analysis={msg.careerData} userName={resumeData.personalInfo.name} />
                      )}
                    </div>
                  )}
                  {msg.jobListings && (
                    <div className="my-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {msg.jobListings.length > 0 ? (
                        msg.jobListings.map((job, i) => <JobListingCard key={i} job={job} />)
                      ) : (
                        <div className="md:col-span-2 text-center p-4 bg-[#d5e7f3]/40 rounded-xl border border-[#d5e7f3]">
                          <p className="text-[#3C0F6C]/80">No open positions found for that query.</p>
                        </div>
                      )}
                    </div>
                  )}
                  {msg.skillsCard && <SkillsCardDisplay skills={msg.skillsCard} />}
                </>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-lg p-3 rounded-2xl bg-[#d5e7f3]/40 text-[#3C0F6C] border border-[#d5e7f3]">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#3C0F6C] rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-[#3C0F6C] rounded-full animate-pulse [animation-delay:0.2s]"></div>
                  <div className="w-2 h-2 bg-[#3C0F6C] rounded-full animate-pulse [animation-delay:0.4s]"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <form onSubmit={handleSendMessage} className="p-4 border-t border-[#d5e7f3] flex items-center gap-4">
          <div className="flex-grow">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask for career advice or job openings..."
              disabled={isLoading}
            />
          </div>
          <button type="submit" disabled={isLoading || !input.trim()} className="bg-[#3C0F6C] text-[#fafafa] font-semibold p-3 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors disabled:opacity-50">
            <ArrowRight size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

const UpgradeModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-md w-full text-center p-8">
        <div className="w-16 h-16 bg-[#d5e7f3] border border-[#d5e7f3] rounded-full flex items-center justify-center mx-auto">
          <Star className="text-[#3C0F6C]" size={32} />
        </div>
        <h3 className="text-2xl font-bold text-[#3C0F6C] mt-4">Upgrade to Premium</h3>
        <p className="text-[#3C0F6C]/80 mt-2">This is a premium feature. Upgrade your account to unlock advanced AI analysis and personalized career counseling.</p>
        <div className="mt-6 flex gap-4">
          <button onClick={onClose} className="w-full bg-[#d5e7f3] text-[#3C0F6C] font-semibold py-2.5 rounded-xl hover:bg-[#fafafa] border border-[#d5e7f3] transition-colors">
            Maybe Later
          </button>
          <button className="w-full bg-[#3C0F6C] text-[#fafafa] font-semibold py-2.5 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors">
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

const RoadmapStep = ({ step }) => {
  const statusConfig = {
    completed: { icon: <CheckCircle size={20} className="text-[#fafafa]" />, circle: 'bg-[#3C0F6C]', text: 'text-[#3C0F6C]' },
    current: { icon: <Star size={20} className="text-[#3C0F6C]" />, circle: 'bg-[#d5e7f3]', text: 'text-[#3C0F6C] font-bold' },
    next: { icon: <ArrowRight size={20} className="text-[#3C0F6C]" />, circle: 'bg-[#d5e7f3]/60', text: 'text-[#3C0F6C]' },
    future: { icon: <div className="w-2.5 h-2.5 bg-[#d5e7f3] rounded-full"></div>, circle: 'bg-[#fafafa] border border-[#d5e7f3]', text: 'text-[#3C0F6C]/70' },
  };
  const config = statusConfig[step.status];

  return (
    <div className="flex items-center gap-4 relative">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${config.circle}`}>
        {config.icon}
      </div>
      <p className={`text-lg ${config.text}`}>{step.title}</p>
    </div>
  );
};

export default CareerPath;
